import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Wallet, Receipt, TrendingUp, AlertTriangle, ArrowRight } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { api } from "../lib/api";
import { rp, rangeFor, tzOffset, fmtDate, METHODS } from "../lib/format";
import { StatCard, PageWrap } from "../components/common";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";

export const ChartTip = ({ active, payload, label }) =>
  active && payload?.length ? (
    <div className="rounded-lg border bg-popover px-3 py-2 text-xs shadow-lg">
      <div className="font-semibold mb-1">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="font-mono">{p.name}: {p.dataKey === "count" ? p.value : rp(p.value)}</div>
      ))}
    </div>
  ) : null;

export default function Dashboard() {
  const [today, setToday] = useState(null);
  const [week, setWeek] = useState(null);
  const [low, setLow] = useState([]);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    const t = rangeFor("today");
    const w = rangeFor("7d");
    api.get("/reports/summary", { params: { ...t, tz_offset: tzOffset() } }).then((r) => setToday(r.data));
    api.get("/reports/summary", { params: { ...w, tz_offset: tzOffset() } }).then((r) => setWeek(r.data));
    api.get("/products", { params: { low_stock: true } }).then((r) => setLow(r.data));
    api.get("/transactions", { params: { limit: 6 } }).then((r) => setRecent(r.data));
  }, []);

  const daily = (week?.daily || []).map((d) => ({ ...d, label: new Date(d.date + "T00:00:00").toLocaleDateString("id-ID", { weekday: "short", day: "numeric" }) }));

  return (
    <PageWrap>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard testid="stat-revenue-today" icon={Wallet} label="Penjualan Hari Ini" value={rp(today?.revenue)} hint={`${today?.count || 0} transaksi`} />
        <StatCard testid="stat-profit-today" icon={TrendingUp} tone="success" label="Laba Kotor Hari Ini" value={rp(today?.profit)} hint={`${today?.items_sold || 0} item terjual`} />
        <StatCard testid="stat-week-revenue" icon={Receipt} tone="chart-5" label="Penjualan 7 Hari" value={rp(week?.revenue)} hint={`Rata-rata ${rp(week?.avg_ticket)}/transaksi`} />
        <StatCard testid="stat-low-stock" icon={AlertTriangle} tone="warning" label="Stok Menipis" value={low.length} hint="produk perlu restock" />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 rounded-2xl border bg-card p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-bold">Tren Penjualan 7 Hari</h2>
            <Button asChild variant="ghost" size="sm" data-testid="dashboard-to-reports"><Link to="/laporan">Laporan <ArrowRight className="h-4 w-4 ml-1" /></Link></Button>
          </div>
          <div className="h-72">
            <ResponsiveContainer>
              <AreaChart data={daily}>
                <defs>
                  <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={(v) => (v / 1000).toFixed(0) + "k"} tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} width={44} />
                <Tooltip content={<ChartTip />} />
                <Area type="monotone" dataKey="revenue" name="Penjualan" stroke="hsl(var(--primary))" strokeWidth={2.5} fill="url(#g1)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="rounded-2xl border bg-card p-5">
          <h2 className="font-display font-bold mb-4">Stok Menipis</h2>
          <div className="space-y-2 max-h-72 overflow-y-auto thin-scroll" data-testid="low-stock-list">
            {low.length === 0 && <p className="text-sm text-muted-foreground">Semua stok aman.</p>}
            {low.map((p) => (
              <div key={p.id} className="flex items-center justify-between rounded-lg bg-muted/50 px-3 py-2">
                <span className="text-sm truncate mr-2">{p.name}</span>
                <Badge variant={p.stock === 0 ? "destructive" : "outline"} className="font-mono">{p.stock} {p.unit}</Badge>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="rounded-2xl border bg-card p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-bold">Transaksi Terbaru</h2>
          <Button asChild variant="ghost" size="sm" data-testid="dashboard-to-transactions"><Link to="/transaksi">Semua <ArrowRight className="h-4 w-4 ml-1" /></Link></Button>
        </div>
        <div className="divide-y">
          {recent.map((t) => (
            <div key={t.id} className="flex items-center justify-between py-3 gap-3">
              <div className="min-w-0">
                <div className="font-mono text-sm font-semibold truncate">{t.invoice}</div>
                <div className="text-xs text-muted-foreground">{fmtDate(t.created_at)} · {t.cashier_name}</div>
              </div>
              <div className="text-right">
                <div className="font-mono font-bold">{rp(t.total)}</div>
                <div className="text-xs text-muted-foreground">{METHODS[t.payment_method]}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </PageWrap>
  );
}
